import { useState, useMemo } from "react";
import { sanitizeHtml } from "../lib/sanitize";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Radar as RadarIcon, Check, Clock, ShieldCheck, MailCheck, Smartphone } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";
import PhoneField, { buildE164 } from "@/components/PhoneField";
import { DEFAULT_PHONE_COUNTRY } from "@/lib/phoneCountries";
import { ALL_COUNTRIES } from "@/lib/constants";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { getDeviceFp } from "@/lib/deviceFp";
import { toast } from "sonner";

const deviceType = () => {
  const ua = navigator.userAgent || "";
  if (/Mobi|Android|iPhone|iPad|iPod/i.test(ua)) return /iPad|Tablet/i.test(ua) ? "tablet" : "mobile";
  return "desktop";
};

export default function Register() {
  const { trialStart, trialVerifyEmail, trialVerifyPhone, trialResend, trialActivate } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const rt = (k, o) => t(`register.trial.${k}`, o);

  const [step, setStep] = useState("form"); // form | verify
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", country: "France" });
  const [phoneCountry, setPhoneCountry] = useState(DEFAULT_PHONE_COUNTRY);
  const [phoneLocal, setPhoneLocal] = useState("");
  const [consent, setConsent] = useState(false);

  const [trialId, setTrialId] = useState(null);
  const [smsSent, setSmsSent] = useState(false);
  const [emailCode, setEmailCode] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [emailOk, setEmailOk] = useState(false);
  const [phoneOk, setPhoneOk] = useState(false);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const meta = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    return {
      referrer: document.referrer || null,
      utm_source: p.get("utm_source"), utm_medium: p.get("utm_medium"), utm_campaign: p.get("utm_campaign"),
      entry_page: window.location.pathname, language: (i18n.language || "fr").slice(0, 2), device: deviceType(),
    };
  }, []);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submitForm = async (e) => {
    e.preventDefault();
    setError("");
    if (!consent) { setError(rt("err.consent")); return; }
    const phone = buildE164(phoneCountry, phoneLocal);
    if (!phone) { setError(rt("err.phone")); return; }
    if ((form.password || "").length < 6) { setError(rt("err.password")); return; }
    setLoading(true);
    try {
      const res = await trialStart({
        first_name: form.first_name, last_name: form.last_name, email: form.email, password: form.password,
        phone, phone_country: phoneCountry, country: form.country, consent: true,
        device_fp: getDeviceFp(), ...meta,
      });
      setTrialId(res.trial_id);
      setSmsSent(!!res.sms_sent);
      setStep("verify");
      toast.success(res.email_sent ? rt("toast.codeSent") : rt("toast.registered"));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || rt("err.generic"));
    } finally { setLoading(false); }
  };

  const verifyEmail = async () => {
    setError("");
    try {
      const r = await trialVerifyEmail(trialId, emailCode.trim());
      setEmailOk(true);
      // Le SMS n'est envoyé qu'APRÈS vérification de l'e-mail : on révèle alors l'étape SMS.
      setSmsSent(!!(r && r.phone_required));
      toast.success(rt("toast.emailVerified"));
    }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || rt("err.emailCode")); }
  };
  const verifyPhone = async () => {
    setError("");
    try { await trialVerifyPhone(trialId, phoneCode.trim()); setPhoneOk(true); toast.success(rt("toast.phoneVerified")); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || rt("err.smsCode")); }
  };
  const [resending, setResending] = useState("");
  const resend = async (channel) => {
    setError(""); setResending(channel);
    try {
      const r = await trialResend(trialId, channel);
      if (channel === "sms") { setSmsSent(true); toast.success(r.sms_sent ? rt("toast.smsResent") : rt("toast.smsUnavailable")); }
      else toast.success(rt("toast.emailResent"));
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || rt("err.resend")); }
    finally { setResending(""); }
  };

  const activate = async () => {
    setError("");
    setLoading(true);
    try {
      await trialActivate(trialId);
      toast.success(rt("toast.activated"));
      navigate("/radar");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || rt("err.activate"));
    } finally { setLoading(false); }
  };

  const canActivate = emailOk && (!smsSent || phoneOk);

  return (
    <div className="flex min-h-screen">
      <div className="relative hidden lg:flex lg:w-1/2 flex-col justify-between bg-[#0F172A] p-14 text-white">
        <div>
          <p className="overline text-[#C6A87C]">Privateer · RADAR</p>
          <h2 className="mt-3 font-display text-5xl leading-tight tracking-tight" dangerouslySetInnerHTML={sanitizeHtml(rt("side.title"))} />
          <span data-testid="trial-real-badge" className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#C6A87C]/40 bg-[#C6A87C]/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.08em] text-[#C6A87C]">
            <ShieldCheck className="h-3.5 w-3.5" /> {t("trial.realBadge")}
          </span>
          <p className="mt-4 max-w-md text-slate-300">
            {rt("side.subtitle")}
          </p>
        </div>
        <ul className="space-y-3 text-slate-300">
          {[
            [Clock, rt("side.b1")],
            [ShieldCheck, rt("side.b2")],
            [MailCheck, rt("side.b3")],
          ].map(([Icon, txt]) => (
            <li key={txt} className="flex items-center gap-3">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#C6A87C]/20 text-[#C6A87C]"><Icon className="h-3.5 w-3.5" /></span>
              {txt}
            </li>
          ))}
        </ul>
      </div>

      <div className="relative flex w-full items-center justify-center bg-white px-6 py-10 lg:w-1/2">
        <div className="absolute right-6 top-6"><LanguageSwitcher variant="dark" /></div>

        {step === "form" ? (
          <form onSubmit={submitForm} className="w-full max-w-sm" data-testid="trial-form">
            <div className="flex items-center gap-2 text-[#C6A87C]"><RadarIcon className="h-5 w-5" /><span className="overline">{rt("form.kicker")}</span></div>
            <h1 className="mt-2 font-display text-3xl tracking-tight text-[#0F172A]">{rt("form.title")}</h1>
            <p className="mt-2 text-sm text-slate-500">{rt("form.subtitle")}</p>
            <span data-testid="trial-real-badge-form" className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#C6A87C]/50 bg-[#C6A87C]/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.06em] text-[#8a6d3b]">
              <ShieldCheck className="h-3.5 w-3.5" /> {t("trial.realBadge")}
            </span>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <div><Label className="text-slate-700">{rt("form.firstName")}</Label><Input data-testid="trial-firstname" value={form.first_name} onChange={(e) => setF("first_name", e.target.value)} required className="mt-1.5 h-11" /></div>
              <div><Label className="text-slate-700">{rt("form.lastName")}</Label><Input data-testid="trial-lastname" value={form.last_name} onChange={(e) => setF("last_name", e.target.value)} required className="mt-1.5 h-11" /></div>
            </div>
            <div className="mt-3"><Label className="text-slate-700">{rt("form.email")}</Label><Input type="email" data-testid="trial-email" value={form.email} onChange={(e) => setF("email", e.target.value)} required className="mt-1.5 h-11" /></div>
            <div className="mt-3"><Label className="text-slate-700">{rt("form.password")}</Label><Input type="password" data-testid="trial-password" value={form.password} onChange={(e) => setF("password", e.target.value)} required minLength={6} className="mt-1.5 h-11" /></div>
            <div className="mt-3">
              <Label className="text-slate-700">{rt("form.phone")}</Label>
              <div className="mt-1.5"><PhoneField country={phoneCountry} local={phoneLocal} onCountryChange={setPhoneCountry} onLocalChange={setPhoneLocal} testid="trial-phone" /></div>
            </div>
            <div className="mt-3">
              <Label className="text-slate-700">{rt("form.country")}</Label>
              <select data-testid="trial-country" value={form.country} onChange={(e) => setF("country", e.target.value)}
                className="mt-1.5 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm outline-none focus:border-[#C6A87C]">
                {ALL_COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <label className="mt-4 flex items-start gap-2.5 text-sm text-slate-600">
              <Checkbox data-testid="trial-consent" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" />
              <span>{rt("form.consentPre")} <a href="/conditions-generales" target="_blank" rel="noopener noreferrer" className="underline" data-testid="trial-cgu-link">{rt("form.consentLink")}</a> {rt("form.consentPost")}</span>
            </label>

            {error && <p className="mt-4 text-sm text-red-600" data-testid="trial-error">{error}</p>}

            <Button type="submit" data-testid="trial-submit" disabled={loading} className="mt-6 h-11 w-full rounded-full bg-[#0F172A] text-white hover:bg-[#1e293b]">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : rt("form.submit")}
            </Button>
            <p className="mt-6 text-center text-sm text-slate-500">{rt("form.already")} <Link to="/login" data-testid="to-login-link" className="font-medium text-[#0F172A] underline">{rt("form.login")}</Link></p>
          </form>
        ) : (
          <div className="w-full max-w-sm" data-testid="trial-verify">
            <div className="flex items-center gap-2 text-[#C6A87C]"><ShieldCheck className="h-5 w-5" /><span className="overline">{rt("verify.kicker")}</span></div>
            <h1 className="mt-2 font-display text-3xl tracking-tight text-[#0F172A]">{rt("verify.title")}</h1>
            <p className="mt-2 text-sm text-slate-500">{smsSent ? rt("verify.subtitleSms") : rt("verify.subtitle")}</p>

            <div className="mt-6 rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700"><MailCheck className="h-4 w-4 text-[#C6A87C]" /> {rt("verify.emailCode")} {emailOk && <Check className="h-4 w-4 text-emerald-600" />}</div>
              <div className="mt-2 flex gap-2">
                <Input data-testid="trial-email-code" value={emailCode} onChange={(e) => setEmailCode(e.target.value)} placeholder="123456" disabled={emailOk} maxLength={6} className="h-11" />
                <Button type="button" data-testid="trial-verify-email" onClick={verifyEmail} disabled={emailOk || emailCode.length < 6} variant="outline" className="h-11 rounded-lg">{rt("verify.verify")}</Button>
              </div>
              {!emailOk && (
                <button type="button" data-testid="trial-resend-email" onClick={() => resend("email")} disabled={resending === "email"}
                  className="mt-2 text-xs font-medium text-[#C6A87C] underline disabled:opacity-50">
                  {resending === "email" ? rt("verify.sending") : rt("verify.resendEmail")}
                </button>
              )}
            </div>

            {smsSent && (
              <div className="mt-3 rounded-xl border border-slate-200 p-4">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-700"><Smartphone className="h-4 w-4 text-[#C6A87C]" /> {rt("verify.smsCode")} {phoneOk && <Check className="h-4 w-4 text-emerald-600" />}</div>
                <div className="mt-2 flex gap-2">
                  <Input data-testid="trial-phone-code" value={phoneCode} onChange={(e) => setPhoneCode(e.target.value)} placeholder="123456" disabled={phoneOk} maxLength={6} className="h-11" />
                  <Button type="button" data-testid="trial-verify-phone" onClick={verifyPhone} disabled={phoneOk || phoneCode.length < 6} variant="outline" className="h-11 rounded-lg">{rt("verify.verify")}</Button>
                </div>
                {!phoneOk && (
                  <button type="button" data-testid="trial-resend-sms" onClick={() => resend("sms")} disabled={resending === "sms"}
                    className="mt-2 text-xs font-medium text-[#C6A87C] underline disabled:opacity-50">
                    {resending === "sms" ? rt("verify.sending") : rt("verify.resendSms")}
                  </button>
                )}
              </div>
            )}

            {error && <p className="mt-4 text-sm text-red-600" data-testid="trial-error">{error}</p>}

            <Button type="button" data-testid="trial-activate" onClick={activate} disabled={!canActivate || loading} className="mt-6 h-11 w-full rounded-full bg-[#C6A87C] text-[#0F172A] hover:brightness-105 disabled:opacity-50">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : rt("verify.activate")}
            </Button>
            <p className="mt-3 text-center text-xs text-slate-400">{rt("verify.footnote")}</p>
          </div>
        )}
      </div>
    </div>
  );
}
